import React, {Component} from 'react'
import {Trans, withNamespaces} from 'react-i18next'
import {get, filter, flowRight as compose} from 'lodash'
import {graphql} from 'react-apollo'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import FormHelperText from '@material-ui/core/FormHelperText'
import {withStyles} from '@material-ui/core/styles'
import messages from '../../../../../assets/messages'
import {UPDATE_OWN_ACCOUNT_PASSWORD_MUTATION} from '../../../../../graphql/mutations'
import {TRADING_PASSWORD_ACCOUNTS_QUERY} from '../../../../../graphql/queries'
import AccountSelect from '../../../../Common/AccountSelect'
import LoadingButton from '../../../../Common/LoadingButton'
import NotificationBar from '../../../../Common/NotificationBar'
import {Password} from '../../../../Common/Password'
import {ErrorList} from '../ErrorList'
import PasswordRequirements from '../PasswordRequirements'

const styles = theme => ({
  formControl: {
    display: 'block',
    margin: theme.spacing(1),
    width: '100%'
  },
  errorMessage: {
    color: theme.palette.error.main,
    display: 'inline-block',
    verticalAlign: 'bottom',
    margin: '13px 13px 13px 0'
  },
  marginTop: {
    marginTop: '-15px'
  },
  note: {
    margin: '10px 0px',
    fontStyle: 'italic'
  },
})

/**
 * The accounts the dropdown may offer. `canChangePassword` is decided by the backend, so the
 * dashboard never carries a second copy of the product rules — wallets, the exchange and the
 * partner portal logins simply never arrive marked. `pendingAmount` is what AccountSelect reads
 * to annotate a balance; this form shows no balance, so nothing is pending to show either.
 */
export const selectableAccounts = (accounts) =>
  filter(accounts, 'canChangePassword').map((account) => ({...account, pendingAmount: 0}))

export class TradingAccountPassword extends Component {
  state = {
    accountId: '',
    currentPassword: '',
    newPassword: '',
    confirmationPassword: '',
    showCurrentPassword: false,
    showNewPassword: false,
    showConfirmationPassword: false,
    messageErrorCurrentPassword: false,
    messageErrorNewPassword: false,
    messageErrorConfirmPassword: false,
    submitMessageError: '',
    loading: false,
    status: {},
  }

  handleChange(prop, event) {
    this.setState({
      [prop]: event.target.value,
      status: {},
      submitMessageError: '',
      ...(
        ['newPassword', 'confirmationPassword'].includes(prop) ? {
          messageErrorConfirmPassword: undefined,
          messageErrorNewPassword: undefined,
          passwordValidationResult: undefined,
        } : {}
      ),
    })
  }

  handleClickShowPassword(prop) {
    this.setState(state => ({[prop]: !state[prop]}))
  }

  /**
   * The dropdown opens on the first account rather than on a blank row, so `accountId` is only
   * set once the client picks another one.
   */
  get selectedAccountId() {
    return this.state.accountId || get(this.props.accounts, '[0].id')
  }

  changePassword = () => {
    const {currentPassword, newPassword, confirmationPassword} = this.state
    const accountId = this.selectedAccountId
    const {t} = this.props

    if (!accountId || !currentPassword || !newPassword || !confirmationPassword) return

    this.setState({status: {}, loading: true})
    this.props.updateOwnAccountPassword({
      variables: {accountId, currentPassword, newPassword, confirmationPassword}
    }).then((succ) => {
      const result = get(succ, 'data.updateOwnAccountPassword')
      if (result.isValid) {
        // The password fields are cleared on purpose: the next change is for another
        // account as often as not, and a filled-in form invites changing the wrong one.
        this.setState({
          loading: false,
          currentPassword: '',
          newPassword: '',
          confirmationPassword: '',
          passwordValidationResult: undefined,
          status: {changePassword: 'success'},
          submitMessageError: ''
        })
      } else {
        this.setState({
          loading: false,
          messageErrorNewPassword: t(messages.passwordValidationError.i18nKey, messages.passwordValidationError.defaults),
          messageErrorConfirmPassword: t(messages.passwordValidationError.i18nKey, messages.passwordValidationError.defaults),
          passwordValidationResult: result
        })
      }
    }).catch((err) => {
      if (err.networkError) {
        this.setState({loading: false, status: {changePassword: 'failure'},
          submitMessageError: t(messages.networkError.i18nKey, messages.networkError.defaults),
        })
      } else if (get(err, 'message', '').includes('accountPasswordChangeFailed')) {
        // The trading platform refused the change. The profile password is untouched, so
        // saying so plainly is better than blaming the password the client just typed.
        this.setState({loading: false, status: {changePassword: 'failure'},
          submitMessageError: t(messages.tradingAccountPasswordChangeFailed.i18nKey,
            messages.tradingAccountPasswordChangeFailed.defaults),
        })
      } else {
        this.setState({
          loading: false,
          messageErrorCurrentPassword: t(messages.oldPasswordIncorrect.i18nKey, messages.oldPasswordIncorrect.defaults),
          passwordValidationResult: undefined
        })
      }
    })
  }

  render() {
    const {classes, t, accounts, locale, accountsLoading} = this.props
    const {currentPassword, newPassword, confirmationPassword, showCurrentPassword, showNewPassword,
      showConfirmationPassword, messageErrorCurrentPassword, messageErrorNewPassword, messageErrorConfirmPassword,
      loading, status, submitMessageError} = this.state

    if (accountsLoading) return null

    if (!accounts.length) {
      return <Grid item xs={12} sm={10} lg={8}>
        <Typography variant="body1"><Trans {...messages.noTradingAccountsForPassword} /></Typography>
      </Grid>
    }

    return (
      <Grid container>
        {status.changePassword === 'success' &&
          <Grid item xs={12} sm={10}>
            <NotificationBar
              onClose={() => this.setState({status: {changePassword: null}})}
              status='success'>
              <Trans {...messages.tradingAccountPasswordChanged} />
            </NotificationBar>
          </Grid>
        }
        <Grid item xs={12} sm={10} lg={8}>
          <Typography variant="caption" color="secondary" className={classes.note}>
            <Trans {...messages.tradingAccountPasswordNote} />
          </Typography>
          <Grid container className={classes.marginTop}>
            <Grid item xs={12}>
              <AccountSelect
                id='tradingAccountSelect'
                name='accountId'
                accounts={accounts}
                label={t(messages.passwordTradingAccount.i18nKey, messages.passwordTradingAccount.defaults)}
                value={this.selectedAccountId}
                onChange={(e) => this.handleChange('accountId', e)}
                locale={locale}
              />
              <Password
                id={'accountCurrentPassword'}
                classes={classes}
                showPassword={showCurrentPassword}
                onChange={(e) => this.handleChange('currentPassword', e)}
                error={!!messageErrorCurrentPassword}
                onClickShow={() => this.handleClickShowPassword('showCurrentPassword')}
                fullWidth
                errorText={messageErrorCurrentPassword}
                label={'currentPassword'}
                value={currentPassword}
                showErrorText={true}
              />
              <Password
                id={'accountNewPassword'}
                classes={classes}
                showPassword={showNewPassword}
                onChange={(e) => this.handleChange('newPassword', e)}
                error={!!messageErrorNewPassword}
                onClickShow={() => this.handleClickShowPassword('showNewPassword')}
                fullWidth
                errorText={messageErrorNewPassword}
                label={'newPassword'}
                value={newPassword}
                showErrorText={true}
              />
              <Password
                id={'accountConfirmationPassword'}
                classes={classes}
                showPassword={showConfirmationPassword}
                onChange={(e) => this.handleChange('confirmationPassword', e)}
                error={!!messageErrorConfirmPassword}
                onClickShow={() => this.handleClickShowPassword('showConfirmationPassword')}
                fullWidth
                errorText={messageErrorConfirmPassword}
                label={'passwordConfirmation'}
                value={confirmationPassword}
                showErrorText={true}
              />
              {!get(this.state, 'passwordValidationResult.isValid') &&
                <ErrorList passwordValidationResult={this.state.passwordValidationResult} />}
              <LoadingButton
                id='changeAccountPasswordBtn'
                onClick={this.changePassword}
                disabled={loading}
                status={status.changePassword}
              ><Trans {...messages.continue} />
              </LoadingButton>
              {status.changePassword === 'failure' &&
                <FormHelperText className={classes.errorMessage}>{submitMessageError}</FormHelperText>}
            </Grid>
          </Grid>
        </Grid>
        <PasswordRequirements />
      </Grid>
    )
  }
}

export default compose(
  withStyles(styles),
  withNamespaces(),
  graphql(UPDATE_OWN_ACCOUNT_PASSWORD_MUTATION, {
    name: 'updateOwnAccountPassword',
  }),
  graphql(TRADING_PASSWORD_ACCOUNTS_QUERY, {
    props: (props) => ({
      accountsLoading: get(props.data, 'loading'),
      locale: get(props.data, 'viewer.locale'),
      accounts: selectableAccounts(get(props.data, 'viewer.accounts', [])),
    })
  }),
)(TradingAccountPassword)
